import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import { ReactNode } from "react";

import { formatBoolean, isUrl } from "@/shared/text";

import CopyToClipboardButton from "../CopyToClipboardButton";
import { RobotoTypography } from "../RobotoTypography";

import styles from "./Value.module.css";

interface ValueProps {
  value: unknown;
}

/**
 * Component that displays an arbitrary value gracefully
 */
export function Value(props: ValueProps) {
  const { value } = props;
  switch (typeof value) {
    case "undefined":
      return <MetadataDisplay value={"Not Set"} />;
    case "string": {
      // Special case: make URL links clickable.
      if (isUrl(value)) {
        return <LinkDisplay value={value} />;
      }
      return <MetadataDisplay value={value} />;
    }
    case "boolean":
      return <MetadataDisplay value={formatBoolean(value)} />;
    case "object":
    case "number":
      return <MetadataDisplay value={value} />;
    default:
      return (
        <RobotoTypography
          sx={{
            wordBreak: "break-word",
            fontSize: "0.85rem",
          }}
        >
          {value as ReactNode}
        </RobotoTypography>
      );
  }
}

function LinkDisplay({ value }: { value: string }) {
  return (
    <span className={styles.container}>
      <Link target="_blank" rel="noopener noreferrer" href={value}>
        <Box className={styles.linkValue} component="span">
          Link
          <OpenInNewIcon className={styles.openLinkButton} />
        </Box>
      </Link>
      <CopyToClipboardButton className={styles.copyButton} text={value} />
      <div className={styles.spacer} />
    </span>
  );
}

export function MetadataDisplay({ value }: { value: unknown }) {
  const stringValue = typeof value === "string" ? value : JSON.stringify(value);

  return (
    <div className={styles.container}>
      <span className={styles.value}>{stringValue}</span>
      <CopyToClipboardButton className={styles.copyButton} text={stringValue} />
      <div className={styles.spacer} />
    </div>
  );
}
